import React from "react";
import { useFormik } from "formik";
import * as yup from "yup";

import {
  createMergeTokenYupTest,
  getMergeTokenValidationError,
  INVITE_MERGE_TOKEN_FIELDS,
  INVITE_MERGE_TOKEN_KEYS,
  normalizeSingleBraceMergeTokens,
  type MergeTokenField,
} from "../../../utils";
import {
  DEFAULT_EMAIL_MESSAGE,
  DEFAULT_EMAIL_SUBJECT,
  DEFAULT_SEND_MESSAGE,
  EMAIL_SEND_WHEN_COPY,
  EMAIL_SUBJECT_MAX_LENGTH,
} from "../../../utils/sequenceTemplate";
import { Button, Input, Sidebar } from "../../ui";

type MessageVariation = {
  id: string;
  label: string;
  message: string;
};

type SendInMailFormValues = {
  isAiMessageEnabled: boolean;
  subject: string;
  messageVariations: MessageVariation[];
  fallbackMessage: string;
};

export type SendInMailFormPayload = {
  isAiMessageEnabled: boolean;
  subject: string;
  fallbackMessage: string;
  messageVariations: MessageVariation[];
};

export type SendInMailInitialConfig = Partial<SendInMailFormPayload>;
export type SendInMailSidebarVariant = "linkedin" | "email";

type SendInMailSidebarProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (payload?: SendInMailFormPayload) => void;
  initialConfig?: SendInMailInitialConfig | null;
  variant?: SendInMailSidebarVariant;
};

const FIELD_TOKENS: readonly MergeTokenField[] = INVITE_MERGE_TOKEN_FIELDS;

const DEFAULT_NEW_MESSAGE_VARIATIONS: MessageVariation[] = [
  {
    id: "message-a",
    label: "Message A",
    message: DEFAULT_SEND_MESSAGE,
  },
];

const DEFAULT_NEW_EMAIL_VARIATIONS: MessageVariation[] = [
  {
    id: "message-a",
    label: "Message A",
    message: DEFAULT_EMAIL_MESSAGE,
  },
];

function buildInitialValues(
  initialConfig?: SendInMailInitialConfig | null,
  variant: SendInMailSidebarVariant = "linkedin"
): SendInMailFormValues {
  const defaultVariations =
    variant === "email" ? DEFAULT_NEW_EMAIL_VARIATIONS : DEFAULT_NEW_MESSAGE_VARIATIONS;
  const variationsFromConfig = initialConfig?.messageVariations;
  const messageVariations =
    Array.isArray(variationsFromConfig) && variationsFromConfig.length > 0
      ? variationsFromConfig
      : defaultVariations;

  return {
    isAiMessageEnabled: Boolean(initialConfig?.isAiMessageEnabled),
    subject:
      variant === "email"
        ? String(initialConfig?.subject || DEFAULT_EMAIL_SUBJECT)
        : String(initialConfig?.subject || ""),
    messageVariations,
    fallbackMessage: String(initialConfig?.fallbackMessage || ""),
  };
}

const linkedinValidationSchema = yup.object({
  messageVariations: yup
    .array(
      yup.object({
        id: yup.string().required(),
        label: yup.string().required(),
        message: yup
          .string()
          .trim()
          .required("Message text is required.")
          .test("merge-tokens", "", createMergeTokenYupTest(INVITE_MERGE_TOKEN_KEYS) as yup.TestFunction),
      })
    )
    .min(1, "At least one message variation is required.")
    .required(),
});

const emailValidationSchema = linkedinValidationSchema.shape({
  subject: yup
    .string()
    .trim()
    .required("Email subject is required.")
    .max(EMAIL_SUBJECT_MAX_LENGTH, `Subject cannot be more than ${EMAIL_SUBJECT_MAX_LENGTH} characters`)
    .test("merge-tokens", "", createMergeTokenYupTest(INVITE_MERGE_TOKEN_KEYS) as yup.TestFunction),
});

export default function SendInMailSidebar({
  isOpen,
  onClose,
  onSave,
  initialConfig,
  variant = "linkedin",
}: SendInMailSidebarProps) {
  const messageRef = React.useRef<HTMLTextAreaElement | null>(null);
  const isEmail = variant === "email";

  const formik = useFormik<SendInMailFormValues>({
    initialValues: buildInitialValues(initialConfig, variant),
    enableReinitialize: true,
    validationSchema: isEmail ? emailValidationSchema : linkedinValidationSchema,
    validateOnMount: true,
    validateOnChange: true,
    onSubmit: (values) => {
      const primary = values.messageVariations[0];
      const payload: SendInMailFormPayload = {
        isAiMessageEnabled: values.isAiMessageEnabled,
        subject: isEmail
          ? normalizeSingleBraceMergeTokens(values.subject, INVITE_MERGE_TOKEN_KEYS)
          : "",
        fallbackMessage: "",
        messageVariations: primary
          ? [
              {
                ...primary,
                message: normalizeSingleBraceMergeTokens(primary.message, INVITE_MERGE_TOKEN_KEYS),
              },
            ]
          : [],
      };
      onSave(payload);
    },
  });

  React.useEffect(() => {
    if (!isOpen || initialConfig != null) return;
    formik.resetForm({ values: buildInitialValues(null, variant) });
  }, [isOpen, initialConfig, variant]);

  const primaryVariation = formik.values.messageVariations[0];

  const updatePrimaryMessage = (nextMessage: string) => {
    const current = formik.values.messageVariations[0];
    if (!current) return;
    void formik.setFieldValue(
      "messageVariations",
      [{ ...current, message: nextMessage }],
      true
    );
    void formik.setFieldTouched("messageVariations.0.message", true, false);
  };

  const messageError = (() => {
    const variationErrors = formik.errors.messageVariations;
    if (Array.isArray(variationErrors)) {
      const first = variationErrors[0];
      if (first && typeof first === "object" && typeof first.message === "string") {
        return first.message;
      }
    }
    if (typeof variationErrors === "string") return variationErrors;
    return undefined;
  })();
  const liveMessageTokenError = getMergeTokenValidationError(
    primaryVariation?.message ?? "",
    INVITE_MERGE_TOKEN_KEYS
  );
  const messageDisplayError = liveMessageTokenError || messageError;

  const insertToken = (tokenKey: string) => {
    const inner = String(tokenKey || "")
      .trim()
      .replace(/^\{\{?/, "")
      .replace(/\}\}?$/g, "");
    const tokenText = "{{" + inner + "}}";

    if (!primaryVariation) return;
    const currentMessage = primaryVariation.message || "";
    const start = messageRef.current?.selectionStart ?? currentMessage.length;
    const end = messageRef.current?.selectionEnd ?? currentMessage.length;
    const nextMessage = normalizeSingleBraceMergeTokens(
      `${currentMessage.slice(0, start)}${tokenText}${currentMessage.slice(end)}`,
      INVITE_MERGE_TOKEN_KEYS
    );
    updatePrimaryMessage(nextMessage);

    const nextCursor = start + tokenText.length;
    requestAnimationFrame(() => {
      if (!messageRef.current) return;
      messageRef.current.focus();
      messageRef.current.setSelectionRange(nextCursor, nextCursor);
    });
  };

  return (
    <Sidebar isOpen={isOpen} onClose={onClose} title={isEmail ? "Send email" : "Send InMail"} size="medium">
      <form onSubmit={formik.handleSubmit} className="flex h-full flex-col">
        <div className="space-y-4 overflow-y-auto px-1">
          {isEmail ? (
            <>
              <p className="text-sm text-dark-grey">{EMAIL_SEND_WHEN_COPY}</p>
              <Input
                size="sm"
                label="Subject"
                value={formik.values.subject}
                maxLength={EMAIL_SUBJECT_MAX_LENGTH}
                placeholder={DEFAULT_EMAIL_SUBJECT}
                error={
                  formik.touched.subject
                    ? getMergeTokenValidationError(formik.values.subject, INVITE_MERGE_TOKEN_KEYS) ||
                      (typeof formik.errors.subject === "string" ? formik.errors.subject : undefined)
                    : undefined
                }
                onChange={(event) => {
                  void formik.setFieldValue("subject", event.target.value, true);
                }}
                onBlur={() => {
                  formik.setFieldTouched("subject", true, true);
                  const normalized = normalizeSingleBraceMergeTokens(
                    formik.values.subject,
                    INVITE_MERGE_TOKEN_KEYS
                  );
                  if (normalized !== formik.values.subject) {
                    void formik.setFieldValue("subject", normalized, true);
                  }
                }}
              />
            </>
          ) : null}
          {/*
            <Toggle
              checked={formik.values.isAiMessageEnabled}
              onChange={(checked) => formik.setFieldValue("isAiMessageEnabled", checked)}
            />
            <FiMessageSquare className="h-4 w-4 text-dark-grey" />
            <span className="text-sm text-dark-grey">ChatGPT Personalized AI Message</span>
            */}

          <div>
            <textarea
              ref={messageRef}
              className="min-h-[130px] w-full rounded-[8px] border border-light-grey px-3 py-2 text-sm outline-none focus:border-brand-blue focus:ring-1 focus:ring-brand-blue"
              value={primaryVariation?.message ?? ""}
              onChange={(event) => updatePrimaryMessage(event.target.value)}
              onBlur={() => {
                formik.setFieldTouched("messageVariations", true, false);
                formik.setFieldTouched("messageVariations.0.message", true, true);
                const v = formik.values.messageVariations[0]?.message ?? "";
                const n = normalizeSingleBraceMergeTokens(v, INVITE_MERGE_TOKEN_KEYS);
                if (n !== v) updatePrimaryMessage(n);
              }}
              placeholder={isEmail ? "Write the email body..." : "Type your message here..."}
            />
            {messageDisplayError ? (
              <p className="mt-1 text-xs text-red-600">{messageDisplayError}</p>
            ) : null}
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {FIELD_TOKENS.map((token) => (
              <button
                key={token.key}
                type="button"
                className="rounded-[8px] border border-light-grey px-3 py-1 text-xs text-dark-black"
                title={token.help}
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                onClick={() => insertToken(token.key)}
              >
                {token.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between border-t border-light-grey pb-2 pt-4">
          <Button
            type="button"
            variant="btn-red"
            size="sm"
            iconSrc="/icons/x.svg"
            iconClassName="h-4 w-4"
            showBefore
            onClick={onClose}
          >
            Dismiss
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            iconSrc="/icons/correct.svg"
            showBefore
            disabled={!formik.isValid}
          >
            Save
          </Button>
        </div>
      </form>
    </Sidebar>
  );
}
